import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
export function AuditLogTable({
  environment,
  componentRef,
}: {
  environment?: string;
  componentRef?: string;
}) {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [retentionDays, setRetentionDays] = useState<number>();
  const [items, setItems] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [revision, setRevision] = useState(0);
  const [user, setUser] = useState('');
  const [method, setMethod] = useState('');
  const [filters, setFilters] = useState({ actor: '', method: '' });
  async function load(next?: string) {
    setBusy(true);
    setError(undefined);
    try {
      const base = await discovery.getBaseUrl(
        componentRef ? 'template-provisioning' : 'audit-log',
      );
      const path = componentRef
        ? `/environments/${encodeURIComponent(environment!)}/audit-logs`
        : '/';
      const query = new URLSearchParams();
      if (componentRef) query.set('componentRef', componentRef);
      if (next) query.set('cursor', next);
      if (filters.actor) query.set('actor', filters.actor);
      if (filters.method) query.set('method', filters.method);
      const response = await fetchApi.fetch(`${base}${path}?${query}`);
      if (!response.ok)
        throw new Error(`Cannot load audit logs (HTTP ${response.status})`);
      const data = await response.json();
      setItems(current => (next ? [...current, ...data.items] : data.items));
      setCursor(data.cursor);
      setRetentionDays(data.retentionDays);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    setItems([]);
    setCursor(undefined);
    void load();
  }, [componentRef, environment, revision, filters]);
  return (
    <>
      <Typography variant="body2">
        Retention:{' '}
        {retentionDays === undefined ? 'Loading...' : `${retentionDays} days`}.
        Times are shown in your local timezone.
      </Typography>
      <Box
        display="flex"
        alignItems="flex-start"
        gridGap={16}
        my={2}
        flexWrap="wrap"
      >
        <TextField
          id="audit-user-filter"
          label="User"
          variant="outlined"
          size="small"
          value={user}
          onChange={event => setUser(event.target.value)}
          helperText="Search actor by email or user reference"
        />
        <TextField
          select
          id="audit-method-filter"
          label="Method"
          variant="outlined"
          size="small"
          style={{ minWidth: 140 }}
          value={method}
          onChange={event => setMethod(event.target.value)}
        >
          <MenuItem value="">All methods</MenuItem>
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].map(
            value => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ),
          )}
        </TextField>
        <Button
          style={{ minHeight: 40 }}
          disabled={busy}
          color="primary"
          variant="contained"
          onClick={() => setFilters({ actor: user.trim(), method })}
        >
          Apply filters
        </Button>
        <Button
          style={{ minHeight: 40 }}
          disabled={busy}
          onClick={() => {
            setUser('');
            setMethod('');
            setFilters({ actor: '', method: '' });
          }}
        >
          Clear filters
        </Button>
      </Box>
      <Button disabled={busy} onClick={() => setRevision(value => value + 1)}>
        Refresh
      </Button>
      {error && (
        <Typography role="alert" color="error">
          {error}
        </Typography>
      )}
      <Table aria-label="Audit log">
        <TableHead>
          <TableRow>
            {['Time', 'Actor', 'Action', 'Result', 'Resource'].map(title => (
              <TableCell key={title}>{title}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id}>
              <TableCell>{new Date(item.timestamp).toLocaleString()}</TableCell>
              <TableCell>{item.actor}</TableCell>
              <TableCell>{item.action}</TableCell>
              <TableCell>{item.outcome}</TableCell>
              <TableCell>
                {item.resource || item.environment || 'Backstage'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!busy && !error && !items.length && (
        <Typography>No audit events match the selected filters.</Typography>
      )}
      {cursor && (
        <Button disabled={busy} onClick={() => load(cursor)}>
          Load more
        </Button>
      )}
    </>
  );
}
